import React from "react";
import { Row, Col, Container } from "react-bootstrap";
import "./_expenseShimmer.scss";

// Base shimmer block — a single animated placeholder.
// Composed into the skeletons below so the sweep animation is identical everywhere.
export const Shimmer = ({ width = "100%", height = 16, radius = 8, style = {} }) => (
    <span className="exp-shimmer" style={{ width, height, borderRadius: radius, ...style }} />
);

// Generic table skeleton (header row + body rows) used for list/table loads.
export const ExpenseTableShimmer = ({ rows = 6, cols = 5 }) => (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "flex", gap: 16, padding: "14px 18px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
            {Array.from({ length: cols }).map((_, i) => (
                <div key={i} style={{ flex: 1 }}><Shimmer width="60%" height={12} /></div>
            ))}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
            <div key={r} style={{ display: "flex", gap: 16, padding: "16px 18px", borderBottom: "1px solid #f1f5f9" }}>
                {Array.from({ length: cols }).map((_, c) => (
                    <div key={c} style={{ flex: 1 }}><Shimmer width={c === 0 ? "80%" : "55%"} height={14} /></div>
                ))}
            </div>
        ))}
    </div>
);

// A single chart card skeleton (header strip + large chart area).
const ChartCardShimmer = () => (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, overflow: "hidden", height: "100%" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9" }}>
            <Shimmer width="40%" height={14} />
        </div>
        <div style={{ padding: 20 }}>
            <Shimmer width="100%" height={180} radius={12} />
        </div>
    </div>
);

// Dashboard body skeleton: KPI stat cards + two charts + a data table.
// (The filters panel is rendered separately above the loading state, so it is not included here.)
export const ExpenseDashboardShimmer = () => (
    <div>
        <Row className="g-3 mb-3">
            {[0, 1, 2, 3].map((i) => (
                <Col xl={3} md={6} sm={6} key={i} className="mb-3">
                    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderTop: "4px solid #e5e7eb", borderRadius: 16, padding: "18px 20px", boxShadow: "0 4px 12px rgba(0,0,0,0.03)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ flex: 1 }}>
                                <Shimmer width="70%" height={12} style={{ marginBottom: 12 }} />
                                <Shimmer width="50%" height={22} style={{ marginBottom: 10 }} />
                                <Shimmer width="60%" height={10} />
                            </div>
                            <Shimmer width={44} height={44} radius={12} style={{ marginLeft: 12 }} />
                        </div>
                    </div>
                </Col>
            ))}
        </Row>

        <Row className="g-3 mb-3">
            <Col lg={6} className="mb-3"><ChartCardShimmer /></Col>
            <Col lg={6} className="mb-3"><ChartCardShimmer /></Col>
        </Row>

        <ExpenseTableShimmer rows={6} cols={7} />
    </div>
);

// Full-page skeleton for the initial config load (tab header + filters + dashboard body).
export const ExpensePageShimmer = () => (
    <div style={{ background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)", minHeight: "100vh", padding: "24px 28px" }}>
        <Container fluid className="px-0">
            <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.05)", overflow: "hidden", border: "1px solid #e2e8f0" }}>
                {/* Tab header */}
                <div style={{ background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", padding: "16px 24px", display: "flex", gap: 10 }}>
                    {[0, 1, 2, 3].map((i) => <Shimmer key={i} width={130} height={30} radius={8} />)}
                </div>
                {/* Content */}
                <div style={{ padding: 28 }}>
                    {/* Filters block */}
                    <div style={{ background: "#f8fafc", borderRadius: 16, border: "1.5px solid #e2e8f0", marginBottom: 24, padding: "20px 24px", display: "flex", gap: 16, flexWrap: "wrap" }}>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <div key={i} style={{ flex: "1 1 140px" }}>
                                <Shimmer width="50%" height={10} style={{ marginBottom: 8 }} />
                                <Shimmer width="100%" height={38} radius={9} />
                            </div>
                        ))}
                    </div>
                    <ExpenseDashboardShimmer />
                </div>
            </div>
        </Container>
    </div>
);
